import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { EventCard } from '../components/cards/EventCard';
import { usePublicData } from '../hooks/usePublicData';
import { SEO } from '../components/SEO';
import { Calendar as CalendarIcon, ArrowRight, Loader2 } from 'lucide-react';

// Helper function to format date for display
const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day}`;
};

export default function Calendar() {
  const { data: events, loading } = usePublicData('events');

  // Filter out past events and transform for EventCard component
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of today
  
  const displayEvents = events
    .filter(event => {
      if (!event.eventDate) return false;
      const eventDate = new Date(event.eventDate + 'T00:00:00');
      return eventDate >= today; // Only include today and future events
    })
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate)) // Sort by date ascending
    .map(event => {
      // Format time with end time if available
      let timeStr = event.startTime || '';
      if (event.endTime) {
        timeStr = `${event.startTime} - ${event.endTime}`;
      }
      return {
        title: event.title,
        date: formatEventDate(event.eventDate),
        time: timeStr,
        location: event.location || '',
        description: event.description || '',
        featured: event.featured || false,
        sermonTitle: event.sermonTitle || '',
        sermonScripture: event.sermonScripture || ''
      };
    });

  return (
    <Layout>
      <SEO 
        title="Church Calendar"
        description="View upcoming events, services, and gatherings at Geneva United Methodist Church. Sunday worship at 9:30 AM, Sunday School at 10:45 AM."
        url="/calendar"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-primary-900" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CalendarIcon className="w-10 h-10 text-secondary" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="calendar-page-title">
            Church Calendar
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Stay connected with upcoming events, services, and gatherings
          </p>
        </div>
      </section>

      {/* Calendar of Events */}
      <ContentSection bgColor="bg-white">
        <SectionHeader 
          title="Calendar of Events"
          subtitle="Mark your calendar for these special gatherings"
        />
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-secondary" />
          </div>
        ) : displayEvents.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {displayEvents.map((event) => (
              <EventCard key={`cal-event-${event.title}-${event.date}`} {...event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl">
            <p className="text-slate-500">No upcoming events scheduled at this time.</p>
            <p className="text-slate-400 text-sm mt-2">Check back soon for new events!</p>
          </div>
        )}
      </ContentSection>

      {/* CTA */}
      <ContentSection bgColor="bg-white" paddingY="py-16">
        <div className="text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary-900 mb-4">
            Want to Know More About an Event?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Contact our church office for more information about any of our events or to add an event to the calendar.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg group"
            data-testid="calendar-contact-btn"
          >
            Contact Us
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </ContentSection>
    </Layout>
  );
}
